import React, { useState, useRef, useCallback, useEffect } from 'react';
import './App.css';

// ── Script patterns — checked automatically on every validation ───────────────
const SCRIPT_PATTERNS = [
  'ddwl4m2hdecbv.cloudfront.net/b/',
  'b2bjsstore.s3.us-west-2.amazonaws.com/b/',
  's3-us-west-2.amazonaws.com/b2bjsstore/b/',
];

// ── Icons ────────────────────────────────────────────────────────────────────
const Icon = ({ name, size = 16 }) => {
  const icons = {
    pass: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" fill="currentColor" opacity=".15"/>
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M7.5 12l3 3 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    fail: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" fill="currentColor" opacity=".15"/>
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M15 9l-6 6M9 9l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    warn: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M12 2L2 20h20L12 2z" fill="currentColor" opacity=".15"/>
        <path d="M12 2L2 20h20L12 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M12 9v5M12 17v.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    info: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" fill="currentColor" opacity=".15"/>
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M12 11v6M12 8v.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    loading: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className="spin">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" strokeDasharray="40 20" strokeLinecap="round"/>
      </svg>
    ),
    add: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    remove: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    chevron: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    script: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M8 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9l-6-6H8z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M14 3v5a1 1 0 001 1h5M9 13l-2 2 2 2M15 13l2 2-2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  };
  return <span className="icon">{icons[name] || null}</span>;
};

// ── Conversational result copy ─────────────────────────────────────────────
function getResultCopy(result) {
  const { test, status, detail } = result;

  switch (test) {
    case 'script_detection':
      if (status === 'pass') return {
        headline: 'Script found',
        explanation: detail,
        context: 'The script tag was detected on this page, which means installation is in place.',
      };
      if (status === 'warn') return {
        headline: 'Script only loads after user interaction',
        explanation: 'The script wasn\'t present on page load, but appeared after we simulated scrolling and mouse movement.',
        context: 'This will significantly limit visitor identification. Because the script needs user interaction before it fires, anonymous page views — the most common type — won\'t be captured at all. We strongly recommend loading the script unconditionally on page load.',
      };
      return {
        headline: 'Script not found',
        explanation: 'We scanned the page DOM and all network requests but couldn\'t find any matching script tags.',
        context: 'This usually means one of the following:<ul><li>the script hasn\'t been installed yet</li><li>or it was installed recently, but your site cache hasn\'t been cleared yet</li><li>or it\'s behind a consent gate that we couldn\'t unlock automatically</li></ul>If you\'ve just installed it, try clearing your site cache and refreshing the page. If you\'re using a consent manager, make sure the script loads after consent is granted.',
      };

    case 'malformed_snippet':
      return {
        headline: 'Snippet found but appears malformed',
        explanation: `We found ${result.issues?.length === 1 ? 'an issue' : `${result.issues?.length} issues`} in your RB2B snippet that would prevent it from running. Expand for details and fixes.`,
        context: 'Malformed snippets are usually caused by a duplicate script tag, a missing or altered attribute, or a snippet that was truncated during copy-paste. Expand below to see exactly what\'s wrong.',
      };

    case 'cmp_detection':
      return {
        headline: result.cmpName ? `${result.cmpName} detected` : 'No consent management platform found',
        explanation: result.cmpName
          ? `This site uses ${result.cmpName} to manage cookie consent.`
          : 'No consent management platform was found on this site.',
        context: result.cmpName
          ? 'We\'ll check below whether the RB2B script is correctly gated behind that consent flow.'
          : 'This means the script will load freely with no consent gate. Many jurisdictions require users to be notified of cookie usage before scripts like this one run, so we strongly recommend adding a consent management tool. <a href="https://support.rb2b.com/en/articles/11164096-what-is-a-consent-management-tool" target="_blank" title="See our guide on setting up a CMP">See our guide on setting up a CMP</a>.',
      };

    case 'cmp_consent_flow':
      if (status === 'pass' && result.loadedBeforeConsent) return {
        headline: 'Script loads before consent',
        explanation: 'The script fires before the user has accepted cookies.',
        context: 'Depending on your compliance requirements, this may be intentional — but worth double-checking with your legal team.',
      };
      if (status === 'pass') return {
        headline: 'Consent flow working correctly',
        explanation: `We clicked Accept on the ${result.cmpName} banner and the script loaded as expected.`,
        context: 'The script is correctly gated behind consent. Users who accept will fire the script; those who decline won\'t.',
      };
      if (status === 'warn') return {
        headline: 'Couldn\'t click the consent button automatically',
        explanation: detail,
        context: 'This doesn\'t necessarily mean there\'s a problem. Some CMPs use custom or iframe-based banners that are harder to interact with programmatically. Test manually by clicking Accept on your site and checking whether the RB2B script fires afterward.',
      };
      return {
        headline: 'Script didn\'t load after consent',
        explanation: `We accepted consent via ${result.cmpName} but the script still didn't appear.`,
        context: 'This usually means the script isn\'t mapped to the right consent category in your CMP, or there\'s a misconfiguration in your tag manager. Check your CMP\'s category settings and confirm the RB2B script is assigned to a category the user accepted.',
      };

    case 'csp_check':
      if (status === 'pass') return {
        headline: 'No Content Security Policy issues detected',
        explanation: 'No Content Security Policy violations were detected for your script.',
        context: null,
      };
      return {
        headline: 'The site\'s Content Security Policies are blocking your script',
        explanation: 'The site\'s Content Security Policies are blocking your RB2B script from loading.',
        context: `Add the following exceptions to your Content Security Policy and share them with your dev team:<br><br><strong>script-src 'self'</strong><br><code>https://ddwl4m2hdecbv.cloudfront.net/b/ https://b2bjsstore.s3.us-west-2.amazonaws.com/b/ https://s3-us-west-2.amazonaws.com https://b-code.liadm.com/lc2.js https://rp.liadm.com idx.liadm.com liadm.com;</code><br><br><strong>connect-src 'self' 'strict-dynamic'</strong><br><code>https://pro.ip-api.com https://alocdn.com/c/vn3d8u2u/a/xtarget/p.json https://*.liadm.com https://9xgnrndqve.execute-api.us-west-2.amazonaws.com https://a.usbrowserspeed.com;</code><br><br><strong>default-src 'self' strict-dynamic</strong><br><code>https://*.liadm.com;</code><br><br>These can be added to your CSP response header or <span class="accent">&lt;meta http-equiv="Content-Security-Policy"&gt;</span> tag.`,
      };

    case 'script_id_check':
      if (status === 'pass') return {
        headline: 'Script ID matches',
        explanation: `The expected script ID (${result.expectedId}) was found in the script src.`,
        context: null,
      };
      
      const scriptFound = result.idFound === false && result.matchingSrcs?.length > 0;
      
      if (scriptFound) {
        return {
          headline: 'Script ID mismatch',
          explanation: result.foundId
            ? `The script is installed, but the ID on the page (<strong>${result.foundId}</strong>) doesn't match your expected ID (<strong>${result.expectedId}</strong>) — it belongs to a different account. If another team member has already set up RB2B, you may need to merge accounts or point to their existing setup.`
            : `The script is installed, but the ID on the page doesn't match your expected ID (<strong>${result.expectedId}</strong>) — a different account's script may be running. If another team member has already set up RB2B, you may need to merge accounts or point to their existing setup.`,
          context: `Check your tag manager or hardcoded snippet to confirm which script ID is actually being loaded.`,
        };
      }
      
      return {
        headline: 'Script ID mismatch',
        explanation: `No script was found so the script ID couldn't be verified.`,
        context: `Check the script_detection result above — if the script isn't installed at all, install it before troubleshooting the ID.`,
      };

    case 'www_redirect_check':
      if (status === 'info') return {
        headline: 'Redirected to www version',
        explanation: `The URL you submitted (<strong>${result.initialUrl}</strong>) redirected to <strong>${result.finalUrl?.replace(/\/$/, '')}</strong>. Validation was performed on the www version.`,
        context: `You authorized RB2B to run on the naked domain (<strong>${result.initialUrl}</strong>), so it will function without issue on the www version as well as any subdomains.`,
      };
      return {
        headline: 'WWW redirect will break the script',
        explanation: `The URL you submitted (<strong>${result.initialUrl}</strong>) redirects to <strong>${result.finalUrl?.replace(/\/$/, '')}</strong>. Your RB2B account authorizes the www version, but your site loads the non-www version — these are treated as different domains, so the script will fire but the authorized domain does not match and therefore it won't attempt to ID visitors.<br /></br />To fix this, update the authorized domain in your RB2B account settings to match the non-www version of your site (<strong>${result.finalUrl?.replace(/\/$/, '').replace(/^https?:\/\//, '')}</strong>).`,
        context: `Context: Navigate to your Script page and delete <strong>${result.initialUrl}</strong> and then click "+ Add Domain" and add <strong>${result.finalUrl?.replace(/\/$/, '').replace(/^https?:\/\//, '')}</strong>, then make it active.`,
      };

    case 'subscription_check':
      return {
        headline: 'Inactive subscription',
        explanation: 'The script is installed correctly, but your RB2B script/subscription does not appear to be active.',
        context: 'Log in to your RB2B account and check your script and subscription status. You may be out of credits, your script may have been disabled by a teammate, or you may not have an active subscription.',
      };

    case 'cookieyes_script_check':
      return {
        headline: 'CookieYes script is blocked (403)',
        explanation: 'The CookieYes consent script on this site is returning a <strong>403 Forbidden</strong> and never loads.',
        context: 'RB2B waits for CookieYes to grant consent before it fires. Because the CookieYes script fails to load, the consent banner never appears, consent is never given, and RB2B waits indefinitely — so it will never run. Fix the CookieYes installation (a 403 usually means an invalid/expired site key or a domain that isn\'t authorized in your CookieYes account) so the banner loads and consent can be granted.',
      };

    default:
      return { headline: result.title, explanation: detail, context: null };
  }
}

// ── Result Card ───────────────────────────────────────────────────────────────
function ResultCard({ result }) {
  const [expanded, setExpanded] = useState(true);
  const { headline, explanation, context } = getResultCopy(result);
  const statusColors = { pass: 'green', fail: 'red', warn: 'amber', info: 'blue' };
  const color = statusColors[result.status] || 'blue';

  const hasTechnicalDetails =
    (result.matches?.length > 0) ||
    (result.networkMatches?.length > 0) ||
    (result.instances?.length > 1) ||
    (result.missingDomains?.length > 0) ||
    (result.issues?.length > 0);

  const hasDetails = hasTechnicalDetails || !!context;

  return (
    <div className={`result-card result-${color}`} data-status={result.status}>
      <div className="result-header" onClick={() => hasDetails && setExpanded(e => !e)}>
        <div className={`result-icon color-${color}`}>
          <Icon name={result.status} size={20} />
        </div>
        <div className="result-body">
          <div className="result-title">{headline}</div>
          <div className="result-detail" dangerouslySetInnerHTML={{ __html: explanation }} />
        </div>
        {hasDetails && (
          <div className={`result-chevron ${expanded ? 'flipped' : ''}`}>
            <Icon name="chevron" size={16} />
          </div>
        )}
      </div>

      {expanded && hasDetails && (
        <div className="result-expanded">
          {context && (
            <div className="detail-context" dangerouslySetInnerHTML={{ __html: context }} />
          )}

          {result.networkMatches?.length > 0 && (
            <div className="detail-section">
              <div className="detail-label">Network Requests</div>
              {result.networkMatches.map((u, i) => (
                <div key={i} className="code-line"><span className="code-val">{u}</span></div>
              ))}
            </div>
          )}

          {result.instances?.length > 1 && (
            <div className="detail-section">
              <div className="detail-label">All Instances ({result.instances.length})</div>
              {result.instances.map((m, i) => (
                <div key={i} className="code-line"><span className="code-val">{m.src}</span></div>
              ))}
            </div>
          )}



          {result.missingDomains?.length > 0 && (
            <div className="detail-section">
              <div className="detail-label">Missing domains</div>
              {result.missingDomains.map((d, i) => (
                <div key={i} className="code-line">
                  <span className="code-key">{d.directive}</span>
                  <span className="code-val">{d.domain}</span>
                </div>
              ))}
            </div>
          )}

          {result.rawCspHeader && (
            <div className="detail-section">
              <div className="detail-label">Raw CSP header</div>
              <div className="code-line"><span className="code-val">{result.rawCspHeader}</span></div>
            </div>
          )}

          {result.issues?.length > 0 && result.issues.map((issue, i) => {
            const ISSUE_COPY = {
              curly_quotes: {
                label: 'Curly quotes detected',
                what: 'The snippet contains curly (\u201C\u201D) or smart quotes instead of straight quotes (\u0022) around the script URL. Browsers cannot interpret curly quotes, so the script URL is never set and the script never loads.',
                cause: 'This usually happens when the snippet is copied from Slack, iMessage, or another chat tool that auto-formats quotes. It can also happen in some email clients or rich-text editors.',
                fix: 'Get a fresh copy of your snippet directly from the <a href="https://app.rb2b.com" target="_blank">RB2B setup page</a> and paste it into your site without going through a chat tool. If you must share it via chat, paste it inside a code block (in Slack: wrap with backticks) to prevent quote conversion.',
              },
              missing_quotes: {
                label: 'Missing quotes around URL',
                what: 'The script URL is assigned without surrounding quotation marks (e.g. <span class="accent">s.src = https://...</span> instead of <span class="accent">s.src = &quot;https://...&quot;</span>). Without quotes, the browser treats the URL as an identifier, not a string, causing a syntax error.',
                cause: 'Microsoft Teams and Outlook convert URLs into hyperlinks when pasted, stripping the surrounding quotes in the process.',
                fix: 'Get a fresh copy of your snippet directly from the <a href="https://app.rb2b.com" target="_blank">RB2B setup page</a>. Before pasting into your site, verify the URL is wrapped in straight quotes. Avoid copy-pasting code through Teams or Outlook.',
              },
              nested_script_tag: {
                label: 'Snippet nested inside another &lt;script&gt; tag',
                what: 'The RB2B snippet appears to be placed inside an existing <span class="accent">&lt;script&gt;</span> block rather than as its own standalone tag. Nested script tags are invalid HTML — browsers won\'t parse the inner tag as executable code.',
                cause: 'This happens when the snippet is accidentally pasted inside another script block in a tag manager, template file, or CMS widget.',
                fix: 'Move the RB2B snippet out of any existing <span class="accent">&lt;script&gt;</span> block so it stands alone as <span class="accent">&lt;script&gt;...&lt;/script&gt;</span>. It should be inserted directly in the <span class="accent">&lt;head&gt;</span> or just before the closing <span class="accent">&lt;/body&gt;</span> tag, not nested inside another script.',
              },
              inside_style_tag: {
                label: 'Snippet inside a &lt;style&gt; tag',
                what: 'The RB2B snippet appears to be placed inside a <span class="accent">&lt;style&gt;</span> tag. Style tags contain CSS, not JavaScript — the browser will not execute the code.',
                cause: 'This usually happens when the snippet is pasted into the wrong field in a CMS (e.g. a "custom CSS" box instead of a "custom HTML/scripts" box).',
                fix: 'Move the snippet into a <span class="accent">&lt;script&gt;&lt;/script&gt;</span> tag, not a <span class="accent">&lt;style&gt;&lt;/style&gt;</span> tag. In most CMSes, look for a "Custom HTML", "Header scripts", or "Footer scripts" field rather than the "Custom CSS" field.',
              },
            };
            const copy = ISSUE_COPY[issue.type] || { label: issue.detail, what: null, cause: null, fix: null };
            return (
              <div key={i} className="detail-section">
                <div className="detail-label">{copy.label}</div>
                {copy.what  && <div className="detail-context" dangerouslySetInnerHTML={{ __html: `<strong>What we found:</strong> ${copy.what}` }} />}
                {copy.cause && <div className="detail-context" dangerouslySetInnerHTML={{ __html: `<strong>Likely cause:</strong> ${copy.cause}` }} />}
                {copy.fix   && <div className="detail-context" dangerouslySetInnerHTML={{ __html: `<strong>How to fix it:</strong> ${copy.fix}` }} />}
              </div>
            );
          })}

        </div>
      )}
    </div>
  );
}


// ── Auth gate — both ?url= and ?id= must be present and non-empty ─────────────
// Read params once at module level so they're available even after the query
// string is stripped from the address bar below.
const _gateParams = new URLSearchParams(window.location.search);
const _paramUrl = _gateParams.get('url') || '';
const _paramId  = _gateParams.get('scriptId')  || '';
const _paramsFromQS = !!_paramUrl.trim() && !!_paramId.trim();

// Cookie helpers
const COOKIE_URL = 'rb2b_url';
const COOKIE_ID  = 'rb2b_script_id';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 7; // 7 days

function setCookie(name, value) {
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Strict`;
}

function getCookie(name) {
  const match = document.cookie.split('; ').find(row => row.startsWith(name + '='));
  return match ? decodeURIComponent(match.split('=')[1]) : '';
}

// If fresh query string params are present, persist them to cookies then strip
// the URL. If not, fall back to whatever is stored in cookies.
let _effectiveUrl = '';
let _effectiveId  = '';

if (_paramsFromQS) {
  _effectiveUrl = _paramUrl;
  _effectiveId  = _paramId;
  setCookie(COOKIE_URL, _paramUrl);
  setCookie(COOKIE_ID, _paramId);
  window.history.replaceState(null, '', window.location.pathname);
} else {
  _effectiveUrl = getCookie(COOKIE_URL);
  _effectiveId  = getCookie(COOKIE_ID);
}

const isAuthorized = !!_effectiveUrl.trim() && !!_effectiveId.trim();

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  // Domain — locked, from the effective URL (query string or cookie fallback).
  const [domain] = useState(() =>
    _effectiveUrl.replace(/^https?:\/\//, '').replace(/\/+$/, '')
  );

  // If the domain from the query string already contains a subdomain (3+ parts,
  // e.g. www.chipstock.com or app.chipstock.com), hide the subdomain input entirely.
  const domainHasSubdomain = domain.split('.').length >= 3;

  // User-editable subdomain prefix — only shown when domain has no existing subdomain
  const [subdomain, setSubdomain] = useState('');

  // User-editable path suffix (e.g. "/pricing")
  const [urlPath, setUrlPath] = useState('');

  // Full URL: https:// + optional subdomain. + domain + optional /path
  const fullUrl = `https://${!domainHasSubdomain && subdomain ? subdomain + '.' : ''}${domain}${urlPath}`;
  // Script ID — from the effective ID (query string or cookie fallback).
  const [scriptId] = useState(() => _effectiveId);
  const [running, setRunning] = useState(false);
  const [statusLog, setStatusLog] = useState([]);
  const [results, setResults] = useState([]);
  const [complete, setComplete] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(true);
  const [error, setError] = useState(null);
  const [pageBlocked, setPageBlocked] = useState(false);
  const [busyCountdown, setBusyCountdown] = useState(0);
  const abortRef = useRef(null);

  const runValidation = useCallback(async () => {
    if (!domain) return;

    window.gtag?.('event', 'debugger_run', {
      event_category: 'debugger',
      domain: domain,
    });

    setRunning(true);
    setResults([]);
    setComplete(false);
    setSummaryExpanded(true);
    setError(null);
    setPageBlocked(false);
    setBusyCountdown(0);
    setStatusLog([]);

    try {
      const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';
      const response = await fetch(`${apiBase}/api/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: fullUrl.trim(), scriptPatterns: SCRIPT_PATTERNS, scriptId: scriptId || undefined }),
        signal: abortRef.current,
      });

      if (!response.ok) {
        if (response.status === 429) {
          setBusyCountdown(5);
          return;
        }
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `Server error ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6);
          if (payload === '[DONE]') {
            setComplete(true);
            setRunning(false);
            return;
          }
          try {
            const msg = JSON.parse(payload);
            if (msg.type === 'status') setStatusLog(prev => [...prev, msg.message]);
            else if (msg.type === 'result') setResults(prev => [...prev, msg]);
            else if (msg.type === 'error') setError(msg.message);
            else if (msg.type === 'page_blocked') setPageBlocked(true);
            else if (msg.type === 'complete') { setComplete(true); }
          } catch {}
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
      }
    } finally {
      setRunning(false);
    }
  }, [domain, fullUrl, scriptId]);

  const subscriptionFailed = results.some(r => r.test === 'subscription_check' && r.status === 'fail');
  // A wrong script ID makes the CDN return 403, which also trips the subscription
  // check. When the ID mismatches, that's the real cause — show all results so the
  // mismatch is visible rather than collapsing to the subscription row alone.
  const idMismatch = results.some(r => r.test === 'script_id_check' && r.status === 'fail');
  // If the script isn't on the site at all, every downstream check (ID, CSP,
  // consent, subscription) is noise — collapse the summary to just that row.
  const scriptNotFound = results.some(r => r.test === 'script_detection' && r.status === 'fail');
  const displayResults = scriptNotFound
    ? results.filter(r => r.test === 'script_detection')
    : (subscriptionFailed && !idMismatch)
      ? results.filter(r => r.test === 'subscription_check')
      : results;

  const passed = displayResults.filter(r => r.status === 'pass').length;
  const failed = displayResults.filter(r => r.status === 'fail').length;
  const warned = displayResults.filter(r => r.status === 'warn').length;
  const info = displayResults.filter(r => r.status === 'info').length;

  // ── Overall verdict (only meaningful once complete) ──────────────────────
  const overallStatus = (() => {
    if (!complete) return null;
    const wwwRedirect    = results.find(r => r.test === 'www_redirect_check');
    const script         = results.find(r => r.test === 'script_detection');
    const subscription   = results.find(r => r.test === 'subscription_check');
    const idCheck        = results.find(r => r.test === 'script_id_check');
    const csp            = results.find(r => r.test === 'csp_check');
    const malformed      = results.find(r => r.test === 'malformed_snippet');
    const cookieYes      = results.find(r => r.test === 'cookieyes_script_check');
    if (wwwRedirect?.status === 'fail' || subscription?.status === 'fail' || script?.status === 'fail' || idCheck?.status === 'fail' || csp?.status === 'fail' || malformed?.status === 'fail' || cookieYes?.status === 'fail') return 'fail';
    if (script?.status === 'warn') return 'warn';
    return 'pass';
  })();

  const overallCopy = (() => {
    if (!overallStatus) return null;
    if (overallStatus === 'pass') return {
      headline: 'Installation verified',
      body: 'Everything looks good! Your RB2B script is installed correctly and you\'re ready to go!<br><br>The debugger does not affect the Verified/Unverified status in your dashboard. That status updates automatically as soon as RB2B detects a visitor, so it may still show "Unverified" temporarily until your first visitors are recorded.',
    };
    if (overallStatus === 'warn') return {
      headline: 'Script requires user interaction',
      body: "The script was found, but only after simulated user interaction — performance will be hindered — please review the information below to resolve this issue and ensure that your install is optimized.",
    };
    const wwwRedirect  = results.find(r => r.test === 'www_redirect_check');
    const script       = results.find(r => r.test === 'script_detection');
    const subscription = results.find(r => r.test === 'subscription_check');
    const idCheck      = results.find(r => r.test === 'script_id_check');
    const csp          = results.find(r => r.test === 'csp_check');
    const malformed    = results.find(r => r.test === 'malformed_snippet');
    const cookieYes    = results.find(r => r.test === 'cookieyes_script_check');
    const body =
      // ID mismatch is checked before subscription: a wrong ID causes the 403 that
      // trips the subscription check, so the mismatch is the actual root cause.
      idCheck?.status      === 'fail' ? `The script is installed but the script ID doesn't match your account${idCheck.foundId ? ` — the ID on the site is ${idCheck.foundId}, which belongs to a different account` : ''}. This also causes the script URL to return a 403, so the subscription may appear inactive.` :
      subscription?.status === 'fail' ? "The script is installed correctly, but your RB2B script does not appear to be active. Please check your subscription status in your RB2B account as you may not have an active subscription, you may be out of credits, or your script may have been disabled by a teammate." :
      wwwRedirect?.status  === 'fail' ? "The URL you provided redirects from www to non-www. Your RB2B account authorizes the www domain, but your site loads without it — these are treated as different domains, so the script won't fire." :
      malformed?.status    === 'fail' ? "We found the snippet but it appears to be malformed — it won't run as-is." :
      cookieYes?.status    === 'fail' ? "The CookieYes consent script is returning a 403 and never loads. RB2B waits for CookieYes to grant consent before firing, so with the banner unable to load, consent is never given and RB2B will never run. Fix the CookieYes installation so its banner loads." :
      script?.status       === 'fail' ? "We couldn't find the script on this page." :
      csp?.status          === 'fail' ? "Your installation looks great but your site's Content Security Policy is blocking the script - please review the information below to resolve this issue or RB2B will not be able to identify visitors on your site." :
      'One or more checks failed.';
    return { headline: 'Installation issue detected', body };
  })();

  const overallColor = overallStatus === 'pass' ? 'green' : overallStatus === 'warn' ? 'amber' : 'red';

  // ── Google Analytics events ───────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthorized) {
      window.gtag?.('event', 'unauthorized_access', {
        event_category: 'security',
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (busyCountdown <= 0) return;
    const id = setTimeout(() => setBusyCountdown(c => c - 1), 1000);
    return () => clearTimeout(id);
  }, [busyCountdown]);

  useEffect(() => {
    if (!complete) return;
    const failedTests = results
      .filter(r => r.status === 'fail' || r.status === 'warn')
      .map(r => r.test)
      .join(', ');
    window.gtag?.('event', 'debugger_result', {
      event_category: 'debugger',
      domain: domain,
      status: overallStatus,
      reason: failedTests || 'all_passed',
    });
  }, [complete]); // eslint-disable-line react-hooks/exhaustive-deps

  // Maintenance page — accessible without auth
  if (window.location.pathname === '/maintenance') {
    return (
      <div className="app">
        <div className="noise" />
        <header className="header">
          <div className="header-inner">
            <div className="logo">
              <Icon name="script" size={22} />
              <span><strong>RB2B</strong> Advanced Installation Debugger</span>
            </div>
          </div>
        </header>
        <main className="main">
          <div className="maintenance-gate">
            <div className="maintenance-icon"><Icon name="warn" size={36} /></div>
            <h1 className="maintenance-title">Down for Maintenance</h1>
            <p className="maintenance-desc">We're making some improvements. The debugger will be back up shortly — check back in a few minutes.</p>
            <p className="maintenance-sub">Questions? Reach us at <a href="mailto:support@rb2b.com" className="maintenance-link">support@rb2b.com</a></p>
          </div>
        </main>
      </div>
    );
  }

  // Security gate — show unauthorized screen if query string is incomplete
  if (!isAuthorized) {
    return (
      <div className="app">
        <div className="noise" />
        <header className="header">
          <div className="header-inner">
            <div className="logo">
              <Icon name="script" size={22} />
              <span><strong>RB2B</strong> Advanced Installation Debugger</span>
            </div>
          </div>
        </header>
        <main className="main">
          <div className="unauth-gate">
            <div className="unauth-icon"><Icon name="fail" size={32} /></div>
            <h1 className="unauth-title">Unauthorized Access</h1>
            <p className="unauth-desc">This tool must be accessed through the RB2B app. Please login and access this tool from your account's Script page.</p>
            <a href="https://app.rb2b.com/login" title="Login to RB2B to access this tool" class="btn">Login to RB2B</a>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="noise" />

      <header className="header">
        <div className="header-inner">
          <div className="logo">
            <Icon name="script" size={22} />
            <span><strong>RB2B</strong> Advanced Installation Debugger</span>
          </div>
        </div>
      </header>

      <main className="main">
        <section className="panel input-panel intro">
          <p>Not seeing visitors yet? This tool scans your site for the most common install blockers, including:</p>
          <ul>
            <li>Script errors or account mismatches</li>
            <li>CSP rules blocking the script</li>
            <li>Lazy loaders delaying activation</li>
            <li>Consent banners preventing it from running</li>
          </ul>
          <p><strong>Beta note:</strong> This is an automated diagnostic and may not catch every configuration. Use the results as guidance, and always manually confirm that the RB2B script is loading and firing on your live site.</p>
        </section>
      
        {/* Input Panel */}
        <section className="panel input-panel">
          <div className="panel-heading">
            <span className="panel-num">01</span>
            <h2>Target URL</h2>
          </div>
          <div className="url-input-row">
            <div className="url-prefix">https://</div>
            {!domainHasSubdomain && (
              <input
                className="url-input url-input-segment"
                type="text"
                placeholder="subdomain"
                value={subdomain}
                onChange={e => setSubdomain(e.target.value.replace(/[./]/g, ''))}
                onKeyDown={e => e.key === 'Enter' && runValidation()}
                disabled={running || !domain}
              />
            )}
            <div className="url-domain">
              {domain || 'no domain'}
            </div>
            <input
              className="url-input url-input-segment"
              type="text"
              placeholder="/path"
              value={urlPath}
              onChange={e => {
                const val = e.target.value;
                // Ensure path always starts with /
                setUrlPath(val && !val.startsWith('/') ? '/' + val : val);
              }}
              onKeyDown={e => e.key === 'Enter' && runValidation()}
              disabled={running || !domain}
            />
          </div>
          {scriptId && (
            <div className="script-id-row">
              <span className="script-id-label">Script ID</span>
              <span className="script-id-value">{scriptId}</span>
            </div>
          )}
        </section>

        {/* Run Button */}
        <button
          className={`btn-run ${running ? 'running' : ''}`}
          onClick={runValidation}
          disabled={running || !domain || busyCountdown > 0}
        >
          {running ? (
            <><Icon name="loading" size={18} /> Debugging your installation…</>
          ) : busyCountdown > 0 ? (
            `Try again in ${busyCountdown}s`
          ) : (
            'Debug Installation'
          )}
        </button>

        {/* Play-by-play progress feed — visible while running, hidden on complete */}
        {running && statusLog.length > 0 && (
          <div className="progress-feed">
            {statusLog.map((msg, i) => (
              <div key={i} className={`progress-line ${i === statusLog.length - 1 ? 'progress-line--active' : 'progress-line--done'}`}>
                <span className="progress-icon">
                  {i === statusLog.length - 1
                    ? <Icon name="loading" size={13} />
                    : <Icon name="pass" size={13} />}
                </span>
                <span className="progress-text">{msg}</span>
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="error-banner">
            <Icon name="fail" size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Page blocked by bot detection */}
        {pageBlocked && (
          <div className="error-banner blocked-banner">
            <Icon name="warn" size={16} />
            <span>
              This site blocked our validator — it may use bot detection that prevents automated checks.
              You'll need to use our legacy validation tool to continue.{' '}
              <a href="https://app.rb2b.com/script" className="blocked-link">Open legacy tool →</a>
            </span>
          </div>
        )}

        {/* 02 Overall Status — shown once complete */}
        {complete && overallStatus && (
          <div className={`result-card result-${overallColor} status-card`}>
            <div className="results-header" >
              <div className="panel-heading">
                <span className="panel-num">02</span>
                <h2>Results</h2>
              </div>
              <div className="results-header-right">
                <div className={`result-icon color-${overallColor}`}>
                  <Icon name={overallStatus} size={28} />
                </div>
                <div className="status-card-headline">{overallCopy.headline}</div>
              </div>
            </div>
            
            <div className="result-body">
              <div className="status-card-body" dangerouslySetInnerHTML={{ __html: overallCopy.body }} />
            </div>
          </div>
        )}

        {/* 03 Summary — collapsible detail cards */}
        {complete && results.length > 0 && (
          <section className="results-section">
            <div
              className="results-header results-header--clickable"
              onClick={() => setSummaryExpanded(e => !e)}
            >
              <div className="panel-heading">
                <span className="panel-num">03</span>
                <h2>Summary</h2>
              </div>
              <div className="results-header-right">
                <div className="results-summary">
                  {passed > 0 && <span className="badge badge-green">{passed} passed</span>}
                  {warned > 0 && <span className="badge badge-amber">{warned} warning{warned !== 1 ? 's' : ''}</span>}
                  {failed > 0 && <span className="badge badge-red">{failed} failed</span>}
                  {info > 0 && <span className="badge badge-blue">{info} info</span>}
                </div>
                <div className={`result-chevron ${summaryExpanded ? 'flipped' : ''}`}>
                  <Icon name="chevron" size={16} />
                </div>
              </div>
            </div>

            {summaryExpanded && (
              <div className="results-list">
                {displayResults.map((r, i) => <ResultCard key={i} result={r} />)}
              </div>
            )}
          </section>
        )}

        {complete && results.length > 0 && (
          <div className="complete-bar">
            Validation complete — {new Date().toLocaleTimeString()}
          </div>
        )}
      </main>
    </div>
  );
}
